/**
 * Feature Flags Vuex Module
 *
 * This module provides centralized management of feature flags using Vuex.
 * It handles initialization, caching, and provides reactive access to feature flag states.
 */

import { FEATURE_FLAG_KEYS, getFeatureFlags } from '@/request/feature_flag_request';

const state = {
  featureFlags: {
    [FEATURE_FLAG_KEYS.AI_RATING_ENABLED]: false,
    [FEATURE_FLAG_KEYS.AI_RATING_MAX_RETRY]: false,
    [FEATURE_FLAG_KEYS.AI_RATING_CIRCUIT_BREAKER_THRESHOLD]: false
  },
  loading: false,
  error: null,
  initialized: false
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_INITIALIZED(state, initialized) {
    state.initialized = initialized;
  },

  SET_FEATURE_FLAGS(state, flags) {
    state.featureFlags = { ...state.featureFlags, ...flags };
  },

  CLEAR_ERROR(state) {
    state.error = null;
  }
};

const actions = {
  /**
   * Initialize the feature flags
   * Fetches all feature flags from the backend and caches them
   */
  async initFeatureFlags({ commit, state }) {
    if (state.loading) {
      console.log('Feature flags are already loading, skipping...');
      return;
    }

    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      console.log('Initializing feature flags...');
      const response = await getFeatureFlags();

      if (response.data && response.data.data) {
        // Convert array of feature flags to object
        const flagsArray = response.data.data;
        const flagsObject = {};

        flagsArray.forEach(flag => {
          if (flag.featureKey && flag.isEnabled !== undefined) {
            flagsObject[flag.featureKey] = flag.isEnabled;
            console.log(`Feature flag loaded: ${flag.featureKey} = ${flag.isEnabled}`);
          }
        });

        // Update the store with fetched flags
        commit('SET_FEATURE_FLAGS', flagsObject);
        commit('SET_INITIALIZED', true);
        console.log('Feature flags initialized successfully:', state.featureFlags);
      }
    } catch (error) {
      console.error('Failed to initialize feature flags:', error);
      commit('SET_ERROR', error.message || 'Failed to load feature flags');
      // Keep default values (false) on error
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Refresh feature flags from the backend
   */
  async refreshFeatureFlags({ dispatch }) {
    console.log('Refreshing feature flags...');
    await dispatch('initFeatureFlags');
  }
};

const getters = {
  /**
   * Check if a specific feature flag is enabled
   * @param {string} featureKey - The feature key to check
   * @returns {Function} Function that returns whether the feature is enabled
   */
  isFeatureEnabled: (state) => (featureKey) => {
    return state.featureFlags[featureKey] === true;
  },

  /**
   * Check if AI rating feature is enabled
   * @returns {boolean} Whether AI rating is enabled
   */
  isAiRatingEnabled: (state) => {
    return state.featureFlags[FEATURE_FLAG_KEYS.AI_RATING_ENABLED] === true;
  },

  /**
   * Get the current feature flag state
   * @returns {Object} The current feature flags object
   */
  getFeatureFlagsState: (state) => {
    return state.featureFlags;
  },

  /**
   * Check if feature flags are currently loading
   * @returns {boolean} Whether feature flags are loading
   */
  isFeatureFlagsLoading: (state) => {
    return state.loading;
  },

  /**
   * Get the feature flag error, if any
   * @returns {string|null} The error message or null
   */
  getFeatureFlagsError: (state) => {
    return state.error;
  },

  /**
   * Check if feature flags have been initialized
   * @returns {boolean} Whether feature flags have been initialized
   */
  areFeatureFlagsInitialized: (state) => {
    return state.initialized;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};